import React, { useState, useMemo } from "react";
import Dropzone from "react-dropzone";
import { Modal, FormControl, Form } from "react-bootstrap";
import fp from "lodash/fp";
import CloseButton from "components/closeButton";
import RequiredMark from "components/requiredMark";
import { Document } from "models";
import { Button } from "components";
import { trim, trimMidSentence } from "validators";
import Select from "components/select";
import UploadImage from "static/images/upload.png";
import { DocumentTypes } from "types";

import clsx from "clsx";
import "./documentModal.scss";

const DocumentModal = ({ show, onHide, onAddDocument, brandingColor }) => {
	const emptyFileNameErrorMessage = "Field is required";
	const [modalValues, setModalValues] = useState(new Document());
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [displayEmptyfileNameErorr, setDisplayEmptyFilenameErorr] = useState(
		false,
	);
	const [displayEmptyTypeErorr, setDisplayEmptyTypeErorr] = useState(false);

	const handleFieldChange = (name, value) => {
		setModalValues({
			...modalValues,
			[name]: value,
		});
	};

	const handleTypeChange = ({ value }) => {
		handleFieldChange("type", value);

		if (value === "") setDisplayEmptyTypeErorr(true);
		else setDisplayEmptyTypeErorr(false);
	};

	const handleNameChange = e => {
		const trimedValue = trimMidSentence(e.target.value);
		handleFieldChange("name", trimedValue);

		if (!e.target.value) {
			setDisplayEmptyFilenameErorr(true);
		} else {
			setDisplayEmptyFilenameErorr(false);
		}

		return e;
	};

	const onDrop = acceptedFiles => {
		if (acceptedFiles.length <= 0) {
			setError("Size of document is more than 10 MB");
		} else {
			let name = modalValues.name;
			if (!name) {
				// If there's no name - use name without type
				const lastIndexOfDot = (
					acceptedFiles[0].name || ""
				).lastIndexOf(".");
				name =
					lastIndexOfDot > 0
						? acceptedFiles[0].name.substring(0, lastIndexOfDot)
						: acceptedFiles[0].name;
			}

			setError("");
			setModalValues({
				...modalValues,
				file: acceptedFiles[0],
				name,
			});
		}
	};

	const handleHide = () => {
		if (!submitting) {
			setModalValues(new Document());
		}

		setError("");
		setDisplayEmptyFilenameErorr(false);
		setDisplayEmptyTypeErorr(false);

		onHide();
	};

	const handleSubmitDocument = async e => {
		if (e) e.preventDefault();

		if (!modalValues.name) {
			setDisplayEmptyFilenameErorr(true);
		} else if (modalValues.type === "") {
			setDisplayEmptyTypeErorr(true);
		} else if (!submitting && !fp.isEmpty(modalValues.file)) {
			setSubmitting(true);
			await onAddDocument(modalValues);
			setSubmitting(false);

			handleHide();
		}
	};

	const maxSize = 10485760;

	const maxSizeMessage = (
		<span className="collectModalMaxSizeTitle">
			Max Size should not exceed 10 MB
		</span>
	);

	const fileName = modalValues.file
		? modalValues.file.name
		: modalValues.name;

	const fileNameMessage = fileName && (
		<span className="collectModalFileName">{fileName}</span>
	);

	const documentTypes = DocumentTypes.getDDLOptions();

	const isEmptyFile = fp.isEmpty(modalValues.file);
	const isEmptyError = !fp.isEmpty(error);

	const isSubmitDisabled = useMemo(
		() =>
			submitting ||
			isEmptyFile ||
			modalValues.type === "" ||
			isEmptyError,
		[isEmptyError, isEmptyFile, modalValues.type, submitting],
	);

	return (
		<Modal
			className="special_modal"
			show={show}
			onHide={handleHide}
			animation={false}
			size="lg"
		>
			<Modal.Header>
				<Modal.Title>Add new attachment</Modal.Title>
				<CloseButton onClick={handleHide} />
			</Modal.Header>
			<Modal.Body>
				<div className="wrappUpload">
					<Dropzone
						onDrop={onDrop}
						minSize={0}
						maxSize={maxSize}
						disabled={submitting || !show}
					>
						{({ getRootProps, getInputProps }) => {
							const inputProps = {
								...getInputProps(),
								multiple: false,
							};

							return (
								<>
									<div {...getRootProps()}>
										<input
											{...inputProps}
											id="file-input"
										/>
										<img src={UploadImage} alt="" />
										<p
											className="modalUpload"
											style={{
												color: fp.isEmpty(
													modalValues.file,
												)
													? "white"
													: "#feffff4d",
											}}
										>
											Click to Choose File or Drag and
											Drop to upload
										</p>
									</div>
									<div className="wrapName">
										{fileNameMessage || maxSizeMessage}
									</div>
									<p className="collectModalLargeTitle">
										{error || ""}
									</p>
								</>
							);
						}}
					</Dropzone>
				</div>
				<Form
					className="collectForm mt-4"
					onSubmit={handleSubmitDocument}
				>
					<h4>
						Name the File
						<RequiredMark />
					</h4>
					<FormControl
						tabIndex={1}
						className={clsx(
							"collectInput",
							displayEmptyfileNameErorr && "error",
							brandingColor && "brandingColor",
						)}
						placeholder="Enter File Name"
						onChange={handleNameChange}
						value={modalValues.name || ""}
						disabled={submitting}
						aria-label="Username"
						onBlur={e => (e.target.value = trim(e.target.value))}
					/>
					{displayEmptyfileNameErorr ? (
						<p className="fileNameError">
							{emptyFileNameErrorMessage}
						</p>
					) : null}
					<h4 className="mt-4">
						Type of Attachment
						<RequiredMark />
					</h4>
					<Select
						options={documentTypes}
						getOptionLabel={type => type.displayName}
						maxMenuHeight={200}
						menuPlacement="top"
						isSearchable={false}
						placeholder="Please select..."
						onChange={handleTypeChange}
						tabIndex={2}
					/>
					{displayEmptyTypeErorr && (
						<p className="fileNameError">
							{emptyFileNameErrorMessage}
						</p>
					)}
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button
					onClick={handleSubmitDocument}
					disabled={isSubmitDisabled}
				>
					Attach
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default DocumentModal;
